import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../../../lib/utils";

const HOLD_MS = 500;

/**
 * A button that fires only after being held down.
 *
 * Taking over a call puts a human voice on a customer's line and silences the AI, which is
 * not something to do on a stray click. A confirmation dialog would also prevent that, but
 * it costs a second click every time and trains people to dismiss dialogs without reading.
 * Holding is one gesture, self-explanatory from the filling bar, and impossible to do by
 * accident.
 */
export function HoldButton({
  onHold,
  disabled,
  children,
  className,
  title,
  testId,
}: {
  onHold: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  title?: string;
  testId?: string;
}) {
  const [progress, setProgress] = useState(0);
  const frameRef = useRef<number | null>(null);
  // Set from fire until release: keeps the bar full and stops a held key firing twice.
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    firedRef.current = false;
    setProgress(0);
  }, []);

  const start = useCallback(() => {
    if (disabled || frameRef.current !== null || firedRef.current) return;
    const startedAt = performance.now();

    const tick = () => {
      const elapsed = (performance.now() - startedAt) / HOLD_MS;
      if (elapsed >= 1) {
        frameRef.current = null;
        firedRef.current = true;
        setProgress(1);
        onHold();
        return;
      }
      setProgress(elapsed);
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
  }, [disabled, onHold]);

  useEffect(() => {
    // A disabled button gets no pointerup, so cancel a hold in progress.
    if (disabled && frameRef.current !== null) cancel();
    // Once usable again, clear the bar left full by the last fire.
    if (!disabled && firedRef.current) cancel();
  }, [disabled, cancel]);

  // rAF pauses in a background tab; on return the hold would look complete and fire.
  useEffect(() => {
    const drop = () => {
      if (frameRef.current !== null) cancel();
    };
    document.addEventListener("visibilitychange", drop);
    window.addEventListener("blur", drop);
    return () => {
      document.removeEventListener("visibilitychange", drop);
      window.removeEventListener("blur", drop);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [cancel]);

  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      data-testid={testId}
      onPointerDown={(event) => {
        if (event.button > 0) return; // right or middle click
        start();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      // Keyboard users get the same gesture: hold the key, release to cancel.
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          if (!event.repeat) start();
        }
      }}
      onKeyUp={cancel}
      onBlur={cancel}
      className={cn(
        "relative flex select-none items-center gap-1.5 overflow-hidden rounded-lg px-3 py-2 text-xs font-medium transition disabled:opacity-40",
        className,
      )}
    >
      {/* No width transition: it lagged, so the bar fired at ~70% and slid back. */}
      <span
        aria-hidden
        data-testid={testId ? `${testId}-fill` : undefined}
        className="absolute inset-y-0 left-0 bg-white/30"
        style={{ width: `${progress * 100}%` }}
      />
      <span className="relative flex items-center gap-1.5">{children}</span>
    </button>
  );
}
